import { useCallback, useEffect, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import CircularProgress from '@mui/joy/CircularProgress'
import Stack from '@mui/joy/Stack'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import type { DatExecutorPoolSnapshot } from '@numeo/types-core'
import { liveDatSearchApi } from 'api/liveDatSearch'

/**
 * The extension browsers that stream DAT live for other users' mobile searches, right now (ADR-046): how many are in
 * the pool and online, how many lanes they stream, and how close they are to the per-browser cap. Read from Redis
 * through main-service when the card opens and on Refresh; nothing here is history.
 */
export default function LiveDatExecutorsCard() {
    const [pool, setPool] = useState<DatExecutorPoolSnapshot | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(false)
    const [updatedAt, setUpdatedAt] = useState<Date | null>(null)

    const load = useCallback(() => {
        setLoading(true)
        liveDatSearchApi
            .executors()
            .then((next) => {
                setPool(next)
                setError(null)
                setUpdatedAt(new Date())
            })
            .catch((err: { message?: string; response?: { status?: number; data?: { message?: string } } }) => {
                console.warn('[LiveDatExecutorsCard] executor pool read failed', err.response?.status, err.message)
                // Never leave old numbers up as if they were current. A 503 means Redis or presence could not be read.
                setPool(null)
                const status = err.response?.status
                setError(`${err.response?.data?.message || 'The executor pool cannot be read right now'}${status ? ` (HTTP ${status})` : ''}`)
            })
            .finally(() => setLoading(false))
    }, [])

    useEffect(() => {
        load()
    }, [load])

    const online = pool ? pool.online.streamable + pool.online.unknown : 0
    const atCap = pool ? (pool.onlineByStreams[pool.maxStreamsPerBrowser] ?? 0) : 0

    return (
        <Card variant="outlined">
            <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <div>
                        <Typography level="title-md">Extension executors</Typography>
                        <Typography level="body-sm">
                            Browsers that stream DAT live for other users&apos; mobile searches while their user has DAT open. Read when the page opens; press
                            Refresh for the latest.
                        </Typography>
                    </div>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {updatedAt && <Typography level="body-xs">Updated {updatedAt.toLocaleTimeString()}</Typography>}
                        <Button size="sm" variant="outlined" onClick={load} loading={loading}>
                            Refresh
                        </Button>
                    </Stack>
                </Stack>

                {error && (
                    <Alert color="danger" variant="soft">
                        {error}
                    </Alert>
                )}
                {!pool && !error && <CircularProgress size="sm" />}

                {pool && (
                    <>
                        <Table size="sm" aria-label="DAT executor pool">
                            <thead>
                                <tr>
                                    <th>Browsers</th>
                                    <th style={{ textAlign: 'right' }}>Can stream</th>
                                    <th style={{ textAlign: 'right' }}>Not tested yet</th>
                                    <th style={{ textAlign: 'right' }}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>In the pool</td>
                                    <td style={{ textAlign: 'right' }}>{pool.pool.streamable.toLocaleString()}</td>
                                    <td style={{ textAlign: 'right' }}>{pool.pool.unknown.toLocaleString()}</td>
                                    <td style={{ textAlign: 'right' }}>{(pool.pool.streamable + pool.pool.unknown).toLocaleString()}</td>
                                </tr>
                                <tr>
                                    <td>Online now</td>
                                    <td style={{ textAlign: 'right' }}>{pool.online.streamable.toLocaleString()}</td>
                                    <td style={{ textAlign: 'right' }}>{pool.online.unknown.toLocaleString()}</td>
                                    <td style={{ textAlign: 'right' }}>{online.toLocaleString()}</td>
                                </tr>
                            </tbody>
                        </Table>

                        <Table size="sm" aria-label="DAT live streams">
                            <tbody>
                                <tr>
                                    <td>Lanes streaming now</td>
                                    <td style={{ textAlign: 'right' }}>{pool.activeStreams.toLocaleString()}</td>
                                </tr>
                                <tr>
                                    <td>Cap per browser</td>
                                    <td style={{ textAlign: 'right' }}>{pool.maxStreamsPerBrowser} streams</td>
                                </tr>
                                <tr>
                                    <td>Online browsers at the cap</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {atCap.toLocaleString()} of {online.toLocaleString()}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Free capacity (more streams the online browsers can take)</td>
                                    <td style={{ textAlign: 'right' }}>{pool.freeStreams.toLocaleString()}</td>
                                </tr>
                            </tbody>
                        </Table>

                        <div>
                            <Typography level="title-sm">Online browsers by streams held</Typography>
                            <Typography level="body-xs" sx={{ mb: 1 }}>
                                Streams loads-ingest has sent each browser; a browser may hold fewer if it declined one.
                            </Typography>
                            <Table size="sm" aria-label="Online browsers by streams held">
                                <thead>
                                    <tr>
                                        {pool.onlineByStreams.map((_, streams) => (
                                            <th key={streams} style={{ textAlign: 'right' }}>
                                                {streams === pool.maxStreamsPerBrowser ? `${streams} (full)` : streams}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        {pool.onlineByStreams.map((count, streams) => (
                                            <td key={streams} style={{ textAlign: 'right' }}>
                                                {count.toLocaleString()}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    </>
                )}
            </Stack>
        </Card>
    )
}
